import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import type { ChatMessage, ChatSummary } from '@/features/chats/types/chat'
import { apiClient } from '@/shared/api/client'

export const CHAT_MESSAGES_PAGE_SIZE = 50

function normalizeChatMessageRole(role: string): ChatMessage['role'] {
  const normalizedRole = role.trim().toUpperCase()

  if (normalizedRole === 'ASSISTANT' || normalizedRole === 'SYSTEM') {
    return normalizedRole
  }

  return 'USER'
}

export function normalizeChatMessage(message: {
  id: string
  role: string
  content: string
  createdAt: string
  sourceFreshness?: ChatMessage['sourceFreshness']
  artifacts?: ChatMessage['artifacts']
  approvals?: ChatMessage['approvals']
}): ChatMessage {
  return {
    ...message,
    role: normalizeChatMessageRole(message.role),
    sourceFreshness: message.sourceFreshness ?? [],
    artifacts: message.artifacts ?? [],
    approvals: message.approvals ?? [],
  }
}

export const workspaceChatQueryKeys = {
  all: ['workspace-chats'] as const,
  list: (workspaceId: string) =>
    [...workspaceChatQueryKeys.all, workspaceId] as const,
  messages: (workspaceId: string, chatId: string) =>
    [...workspaceChatQueryKeys.list(workspaceId), 'messages', chatId] as const,
}

export function workspaceChatsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: workspaceChatQueryKeys.list(workspaceId),
    queryFn: async (): Promise<Array<ChatSummary>> => {
      const chats = await apiClient.chats.listWorkspaceChats(workspaceId)
      return [...chats].sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() -
          new Date(left.updatedAt).getTime(),
      )
    },
    enabled: workspaceId.length > 0,
  })
}

/**
 * Cursor-paginated message history. Each page is a chronological (ASC) slice;
 * page 0 is the latest window, subsequent pages are progressively older
 * (loaded on scroll-up via the `before` cursor).
 */
export function chatMessagesInfiniteQueryOptions(
  workspaceId: string,
  chatId: string,
) {
  return infiniteQueryOptions({
    queryKey: workspaceChatQueryKeys.messages(workspaceId, chatId),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined
    }): Promise<Array<ChatMessage>> => {
      const messages = await apiClient.chats.getChatMessages(
        workspaceId,
        chatId,
        { limit: CHAT_MESSAGES_PAGE_SIZE, before: pageParam },
      )
      // Backend already returns this page in chronological order.
      return messages.map(normalizeChatMessage)
    },
    initialPageParam: undefined as string | undefined,
    // Next (older) page starts before the oldest message of the last page.
    getNextPageParam: (lastPage): string | undefined =>
      lastPage.length === CHAT_MESSAGES_PAGE_SIZE
        ? lastPage[0]?.createdAt
        : undefined,
    enabled: workspaceId.length > 0 && chatId.length > 0,
  })
}

/** Flatten infinite pages ([latest, older, …], each ASC) into one ASC list. */
export function flattenChatMessagePages(
  data: InfiniteData<Array<ChatMessage>> | undefined,
): Array<ChatMessage> {
  if (!data) {
    return []
  }

  return [...data.pages].reverse().flat()
}

/** Insert a message or replace its persisted version in the cache. */
export function appendChatMessageToCache(
  queryClient: QueryClient,
  workspaceId: string,
  chatId: string,
  message: ChatMessage,
) {
  queryClient.setQueryData<InfiniteData<Array<ChatMessage>>>(
    workspaceChatQueryKeys.messages(workspaceId, chatId),
    (current) => {
      if (!current) {
        return { pages: [[message]], pageParams: [undefined] }
      }

      if (
        current.pages.some((page) =>
          page.some((item) => item.id === message.id),
        )
      ) {
        return {
          ...current,
          pages: current.pages.map((page) =>
            page.map((item) => (item.id === message.id ? message : item)),
          ),
        }
      }

      const latestPage = current.pages[0] ?? []
      const pages = [...current.pages]
      pages[0] = [...latestPage, message].sort(
        (left, right) =>
          new Date(left.createdAt).getTime() -
          new Date(right.createdAt).getTime(),
      )

      return { ...current, pages }
    },
  )
}

interface UseWorkspaceChatsQueryOptions {
  refetchInterval?:
    | number
    | false
    | ((query: {
        state: { data: Array<ChatSummary> | undefined }
      }) => number | false)
}

export function useWorkspaceChatsQuery(
  workspaceId: string,
  enabled = true,
  options?: UseWorkspaceChatsQueryOptions,
) {
  return useQuery({
    ...workspaceChatsQueryOptions(workspaceId),
    enabled: enabled && workspaceId.length > 0,
    refetchInterval: options?.refetchInterval,
  })
}

export function useChatMessagesInfiniteQuery(
  workspaceId: string,
  chatId: string,
  enabled = true,
) {
  return useInfiniteQuery({
    ...chatMessagesInfiniteQueryOptions(workspaceId, chatId),
    enabled: enabled && workspaceId.length > 0 && chatId.length > 0,
  })
}
